/*
 * Main styles for the subdomain navigation bar.
 */
.SubdomainNavBar {
  position: relative;
  z-index: 90;
  font-weight: 550;
  background: var(--brand-color-canvas-default);
  height: var(--SubdomainNavBar-height);
}

.SubdomainNavBar::after {
  content: '';
  position: absolute;
  z-index: 3;
  inset-inline: 0;
  inset-block-start: 100%;
  height: var(--brand-borderWidth-thin);
  background-color: var(--brand-SubdomainNavBar-border-nav-default);
  pointer-events: none;
}

/*
 * Outer container
 */
.SubdomainNavBar-outer-container {
  --SubdomainNavBar-height: var(--base-size-64);

  z-index: 90;
}

@media screen and (max-width: 47.99rem) {
  .SubdomainNavBar-outer-container {
    --SubdomainNavBar-height: calc(var(--base-size-64) - var(--base-size-8));
  }
}

.SubdomainNavBar-outer-container:not(.SubdomainNavBar-outer-container--fixed) {
  position: relative;
}

.SubdomainNavBar-outer-container--fixed {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
}

/*
 * Inner container
 */
.SubdomainNavBar-inner-container {
  display: flex;
  padding: 4px 28px;
}

.SubdomainNavBar-inner-container--centered {
  max-width: 1280px;
  margin: 0 auto;
}

@media screen and (min-width: 48rem) {
  .SubdomainNavBar-inner-container {
    padding: 4px 32px;
  }
}

.SubdomainNavBar-inner-container--search-open {
  animation: fade-out 150ms;
}

/*
 * Logo and title area
 */
.SubdomainNavBar-title-area {
  display: inline-flex;
  list-style-type: none;
  align-items: center;
  gap: var(--base-size-8);
  padding: 0;
  margin: 0;
  max-width: 340px;
  color: var(--brand-color-text-default);
  padding: var(--base-size-16) 0;
}

@media screen and (min-width: 63.25rem) {
  .SubdomainNavBar-title-area {
    gap: var(--base-size-16);
  }
}

.SubdomainNavBar-logo-mark,
.SubdomainNavBar-title {
  display: inline-flex;
  align-items: center;
  color: var(--brand-color-text-default);
}

.SubdomainNavBar-logo-mark {
  position: relative;
}

.SubdomainNavBar-back-arrow {
  position: absolute;
  display: flex;
  inset-inline-start: calc(-1 * var(--base-size-24) + var(--base-size-4));
  inset-block-start: 50%;
  color: var(--brand-color-text-muted);
  opacity: 0;
  transform: translate(var(--base-size-4), -50%);
  transition: opacity var(--brand-animation-duration-fast), transform var(--brand-animation-duration-fast) ease;
}

.SubdomainNavBar-logo-mark:hover .SubdomainNavBar-back-arrow {
  opacity: 1;
  transform: translate(0, -50%);
}

.SubdomainNavBar-logo-mark svg {
  width: 32px;
  height: 32px;
}

.SubdomainNavBar-title {
  line-height: var(--base-size-32);
  text-decoration: none;
  padding-inline-end: var(--base-size-16);
  white-space: nowrap;
}

.SubdomainNavBar-title:hover {
  text-decoration: none;
}

.SubdomainNavBar--variant-featured .SubdomainNavBar-title-prefix {
  color: var(--brand-color-text-default);
}

.SubdomainNavBar-title-label {
  color: inherit;
}

.SubdomainNavBar--variant-default.SubdomainNavBar--has-title .SubdomainNavBar-title-area > li:first-child {
  display: flex;
  align-items: center;
  align-self: stretch;
}

.SubdomainNavBar--variant-default.SubdomainNavBar--has-title .SubdomainNavBar-title-area > li:first-child::after {
  content: '';
  box-sizing: border-box;
  align-self: center;
  flex: none;
  width: var(--brand-borderWidth-thin);
  height: var(--SubdomainNavBar-height);
  margin-inline: var(--base-size-16);
  background-color: var(--brand-color-border-muted);
}

.SubdomainNavBar-leading-component,
.SubdomainNavBar-trailing-component {
  display: inline-flex;
  align-items: center;
  min-width: 0;
}

.SubdomainNavBar-leading-component {
  margin-inline-end: var(--base-size-16);
}

.SubdomainNavBar-trailing-component {
  margin-inline-start: var(--base-size-16);
}

/*
 * Primary navigation link area
 */
.SubdomainNavBar-primary-nav {
  display: inline-flex;
  list-style-type: none;
  align-items: center;
  flex: 1 1 0;
  min-width: 0;
  padding: 0;
  margin: 0;
}

.SubdomainNavBar-primary-nav-list {
  list-style-type: none;
}

@media screen and (max-width: 767px) {
  .SubdomainNavBar-primary-nav-list {
    display: none;
  }

  .SubdomainNavBar-primary-nav-list--invisible {
    display: none;
  }

  .SubdomainNavBar-primary-nav-list--visible {
    display: block;
    margin: 0;
    padding: 0;
  }

  .SubdomainNavBar-leading-component,
  .SubdomainNavBar-trailing-component {
    flex: none;
    width: 100%;
    box-sizing: border-box;
    padding-block: var(--base-size-16);
    padding-inline: var(--base-size-16);
  }

  .SubdomainNavBar-leading-component {
    margin-inline-end: 0;
  }

  .SubdomainNavBar-trailing-component {
    margin-inline-start: 0;
  }

  .SubdomainNavBar-menu-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow-y: auto;
    position: absolute;
    top: var(--SubdomainNavBar-height);
    left: 0;
    right: 0;
    z-index: 2;
    background-color: var(--brand-color-canvas-default);
    width: 100%;
    max-height: calc(100vh - var(--SubdomainNavBar-height) - var(--SubdomainNavBar-menu-offset-block-start, 0px));
    border-block-end: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
    animation: fade-in-down 500ms;
    animation-timing-function: var(--brand-animation-easing-default);
  }

  .SubdomainNavBar-menu-wrapper--close {
    display: none;
  }

  .SubdomainNavBar-menu-wrapper-footer {
    flex: none;
    display: flex;
    flex-direction: column;
    width: 100%;
  }
}

@media screen and (min-width: 48rem) {
  .SubdomainNavBar-primary-nav-overflow {
    display: flex;
    align-items: center;
    flex: 1 1 0;
    min-width: 0;
    margin: 0 var(--base-size-24) 0 0;
  }

  .SubdomainNavBar-primary-nav-list {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    flex-wrap: nowrap;
    min-width: 0;
    block-size: var(--base-size-48);
    max-block-size: var(--base-size-48);
    overflow: hidden;
    padding: 0;
    margin: 0;
  }
}

.SubdomainNavBar-primary-nav-list-item {
  flex: 0 0 auto;
  white-space: nowrap;
}

@media screen and (max-width: 767px) {
  .SubdomainNavBar-primary-nav-list-item {
    animation-name: fade-in-down-staggered;
    animation-duration: 500ms;
    animation-delay: calc(var(--animation-order) * 80ms);
    animation-fill-mode: both;
    animation-timing-function: var(--brand-animation-easing-default);
  }
}

@media screen and (min-width: 48rem) {
  .SubdomainNavBar-primary-nav-list-item--overflowed {
    visibility: hidden;
    pointer-events: none;
  }

  .SubdomainNavBar-primary-nav-list-item--overflow {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    position: absolute;
    inset-inline-end: 0;
    visibility: hidden;
    pointer-events: none;
  }

  .SubdomainNavBar-primary-nav-overflow--has-overflow .SubdomainNavBar-primary-nav-list-item--overflow {
    position: relative;
    visibility: visible;
    pointer-events: auto;
  }
}

.SubdomainNavBar-link {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--base-size-8);
  color: var(--brand-color-text-default);
  font-family: var(--brand-heading-fontFamily);
  font-weight: var(--base-text-weight-normal);
  text-decoration: none;
}

.SubdomainNavBar-link:hover {
  text-decoration: none;
}

.SubdomainNavBar-link-text {
  position: relative;
}

.SubdomainNavBar-link-content {
  display: inline-flex;
  align-items: center;
  gap: var(--base-size-8);
}

@media screen and (max-width: 767px) {
  .SubdomainNavBar-link--title {
    font-weight: var(--base-text-weight-medium);
  }
}

@media screen and (min-width: 48rem) {
  .SubdomainNavBar .SubdomainNavBar-overflow-menu-item .SubdomainNavBar-link {
    width: 100%;
    min-height: var(--base-size-40);
    padding: var(--base-size-8) var(--base-size-12);
    color: var(--brand-color-text-muted);
    background-color: var(--brand-color-canvas-default);
    border-radius: var(--brand-borderRadius-medium);
    font-size: var(--brand-text-size-100);
    line-height: var(--brand-text-lineHeight-200);
    transition: background-color var(--brand-animation-easing-default) 250ms;
  }

  .SubdomainNavBar .SubdomainNavBar-overflow-menu-item .SubdomainNavBar-link:hover,
  .SubdomainNavBar .SubdomainNavBar-overflow-menu-item .SubdomainNavBar-link:focus,
  .SubdomainNavBar .SubdomainNavBar-overflow-menu-item .SubdomainNavBar-link:focus-visible {
    color: var(--brand-color-text-default);
    background-color: var(--brand-color-canvas-subtle);
  }

  .SubdomainNavBar .SubdomainNavBar-overflow-menu-item .SubdomainNavBar-link:focus,
  .SubdomainNavBar .SubdomainNavBar-overflow-menu-item .SubdomainNavBar-link:focus-visible {
    box-shadow: inset 0 0 0 var(--brand-borderWidth-thick) var(--brand-color-focus);
    outline: none;
  }
}

.SubdomainNavBar-more-link {
  display: none;
}

@media screen and (min-width: 48rem) {
  .SubdomainNavBar-more-link {
    cursor: pointer;
    appearance: none;
    border: none;
    background-color: transparent;
    display: inline-flex;
    align-items: center;
    gap: var(--base-size-4);
  }
}

.SubdomainNavBar .SubdomainNavBar-overflow-menu {
  position: absolute;
  z-index: 100;
  right: auto;
  background-color: var(--brand-color-canvas-default);
  border: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
  border-start-start-radius: 0;
  border-start-end-radius: 0;
  border-end-start-radius: var(--brand-borderRadius-medium);
  border-end-end-radius: var(--brand-borderRadius-medium);
  animation: fade-in-down-overflow var(--brand-animation-duration-faster);
  animation-timing-function: var(--brand-animation-easing-default);
  min-width: calc(var(--base-size-128) + var(--base-size-32));
  transform: translateY(calc(0px - var(--base-size-4)));
  overflow: hidden;
}

.SubdomainNavBar .SubdomainNavBar-overflow-menu-list {
  display: grid;
  gap: var(--brand-borderWidth-thin);
  list-style-type: none;
  padding: var(--base-size-8);
  margin: 0;
  width: 100%;
}

/*
 * Toolbar actions area for search and mobile menu
 */

.SubdomainNavBar-secondary-nav {
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  margin-left: auto;
}
.SubdomainNavBar-search-trigger {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  min-width: 0;
}

@media screen and (min-width: 63.25rem) {
  .SubdomainNavBar-search-trigger {
    --SubdomainNavBar-search-input-width: calc(var(--base-size-128) * 2 + var(--base-size-24));

    flex: 0 9999 var(--SubdomainNavBar-search-input-width);
    width: var(--SubdomainNavBar-search-input-width);
    max-width: 100%;
    min-width: calc(var(--base-size-128) + var(--base-size-32));
  }
}

.SubdomainNavBar-menu-button {
  position: relative;
  cursor: pointer;
  appearance: none;
  background-color: transparent;
  border: var(--brand-borderWidth-thick) solid transparent;
  color: var(--brand-color-text-default);
  width: calc(48px - var(--brand-borderWidth-thick));
  height: calc(48px - var(--brand-borderWidth-thick));
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border 250ms var(--brand-animation-easing-default), box-shadow 250ms ease;
  border-radius: var(--brand-borderRadius-medium);
  box-shadow: 0 0 0 2px transparent;
  gap: 0;
}

.SubdomainNavBar-menu-button svg {
  width: 24px;
  height: 24px;
}

.SubdomainNavBar-search-input-button {
  cursor: pointer;
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--base-size-16);
  width: 100%;
  block-size: var(--base-size-32);
  min-block-size: var(--base-size-32);
  padding: var(--base-size-4) var(--base-size-4) var(--base-size-4) calc(var(--base-size-8) + var(--base-size-2));
  overflow: hidden;
  color: var(--brand-color-text-muted);
  background-color: var(--brand-color-canvas-subtle);
  border: 0;
  border-radius: var(--brand-borderRadius-medium);
  font-family: var(--brand-body-fontFamily);
  transition: background-color var(--brand-animation-duration-fast) var(--brand-animation-easing-default),
    box-shadow var(--brand-animation-duration-fast) var(--brand-animation-easing-default);
}

.SubdomainNavBar-search-input-button:hover {
  box-shadow: inset 0 0 0 var(--brand-borderWidth-thin) var(--brand-color-border-muted);
}

.SubdomainNavBar-search-input-button:focus-visible {
  outline: var(--base-size-4) solid var(--brand-color-focus);
  outline-offset: var(--base-size-2);
}

.SubdomainNavBar-search-input-button:active {
  box-shadow: inset 0 0 0 var(--brand-borderWidth-thin) var(--brand-color-border-default);
}

.SubdomainNavBar-search-input-button-placeholder {
  display: inline-flex;
  align-items: center;
  gap: var(--base-size-8);
  min-width: 0;
  overflow: hidden;
  color: var(--brand-color-text-muted);
  font-size: var(--brand-text-size-100);
  font-weight: var(--base-text-weight-normal);
  line-height: 1.2;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.SubdomainNavBar-search-input-button-placeholder svg {
  flex: none;
}

.SubdomainNavBar-search-input-button-placeholder span {
  overflow: hidden;
  text-overflow: ellipsis;
}

.SubdomainNavBar-search-input-button-shortcut {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  min-width: var(--base-size-24);
  padding-inline: var(--base-size-8);
  color: var(--brand-color-text-default);
  background-color: var(--brand-color-canvas-default);
  border: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
  border-radius: calc(var(--brand-borderRadius-medium) / 2);
  font-family: var(--brand-fontStack-monospace);
  font-size: var(--brand-text-size-100);
  font-weight: var(--base-text-weight-normal);
  line-height: 1.6;
}

@media screen and (max-width: 47.99rem) {
  .SubdomainNavBar-search-trigger {
    flex: 0 0 var(--base-size-48);
    justify-content: center;
    width: var(--base-size-48);
  }

  .SubdomainNavBar-search-input-button {
    justify-content: center;
    gap: 0;
    width: var(--base-size-32);
    padding: var(--base-size-8);
    background-color: transparent;
  }

  .SubdomainNavBar-search-input-button-placeholder {
    gap: 0;
  }

  .SubdomainNavBar-search-input-button-placeholder span,
  .SubdomainNavBar-search-input-button-shortcut {
    display: none;
  }
}

.SubdomainNavBar-menu-button {
  display: inline-flex;
  align-items: center;
  align-self: center;
}

.SubdomainNavBar-menu-button-icon {
  display: inline-flex;
  flex-direction: column;
  gap: calc(var(--base-size-4) - var(--brand-borderWidth-thin));
}

.SubdomainNavBar-menu-button-label {
  display: none;
}

.SubdomainNavBar-menu-button-bar {
  width: 22px;
  height: 2px;
  background-color: var(--brand-color-text-default);
  margin: var(--base-size-4) auto;
  border-radius: var(--brand-borderRadius-large);
  display: block;
}

@media (prefers-reduced-motion: no-preference) {
  .SubdomainNavBar-menu-button-bar {
    transition-timing-function: var(--brand-animation-easing-default);
    transition-duration: 500ms;
    transition-property: opacity, transform;
  }
}

.SubdomainNavBar-menu-button-bar:nth-of-type(1) {
  transform-origin: bottom right;
}

.SubdomainNavBar-menu-button-bar:nth-of-type(3) {
  transform-origin: top right;
}

.SubdomainNavBar-menu-button--close .SubdomainNavBar-menu-button-bar:nth-of-type(1) {
  transform: rotate(-45deg) translateY(-3px);
}

.SubdomainNavBar-menu-button--close .SubdomainNavBar-menu-button-bar:nth-of-type(2) {
  opacity: 0;
  transform: scale(0);
}

.SubdomainNavBar-menu-button--close .SubdomainNavBar-menu-button-bar:nth-of-type(3) {
  transform: rotate(45deg) translateY(3px);
}

.SubdomainNavBar-mobile-menu-button.SubdomainNavBar-menu-button--close {
  position: relative;
  z-index: 3;
  background-color: var(--brand-color-canvas-muted);
}

.SubdomainNavBar-mobile-menu-button.SubdomainNavBar-menu-button--close:focus-visible {
  outline: none;
  box-shadow: none;
  transition: none;
}

.SubdomainNavBar-mobile-menu-button.SubdomainNavBar-menu-button--close:focus-visible::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  border: var(--brand-borderWidth-thick) solid var(--brand-color-focus);
}

.SubdomainNavBar-mobile-menu-button.SubdomainNavBar-menu-button--close::after {
  content: '';
  position: absolute;
  z-index: 1;
  inset-inline: 0;
  inset-block-end: calc(var(--brand-borderWidth-thin) * -2);
  height: calc(var(--brand-borderWidth-thin) * 2);
  background-color: var(--brand-color-canvas-muted);
}

@media screen and (min-width: 63.25rem) {
  .SubdomainNavBar-menu-button:hover {
    background-color: transparent;
    border: var(--brand-borderWidth-thick) solid var(--brand-SubdomainNavBar-border-button-hover);
  }

  .SubdomainNavBar-menu-button:active {
    background-color: transparent;
    box-shadow: 0 0 0 1px var(--brand-SubdomainNavBar-border-button-hover);
  }

  .SubdomainNavBar-mobile-menu-button {
    display: none;
  }
}

@media screen and (min-width: 48rem) and (max-width: 63.24rem) {
  .SubdomainNavBar-primary-nav {
    display: none;
  }

  .SubdomainNavBar-secondary-nav {
    position: relative;
    flex: 0 0 auto;
    align-self: stretch;
  }

  .SubdomainNavBar-search-trigger {
    --SubdomainNavBar-tablet-search-width: calc(var(--base-size-128) * 2 + var(--base-size-2));

    flex: 0 0 var(--SubdomainNavBar-tablet-search-width);
    width: var(--SubdomainNavBar-tablet-search-width);
    padding-inline: var(--base-size-8);
    border-inline-start: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
  }

  .SubdomainNavBar-search-input-button {
    width: 100%;
  }

  .SubdomainNavBar-mobile-menu-button {
    align-self: stretch;
    flex: none;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: var(--base-size-8);
    width: max-content;
    min-width: calc(var(--base-size-64) + var(--base-size-32));
    height: 100%;
    padding-inline: var(--base-size-16);
    color: var(--brand-color-text-default);
    border: 0;
    border-inline-start: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
    border-inline-end: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
    border-radius: 0;
    overflow: visible;
    font-family: var(--brand-body-fontFamily);
    font-size: var(--brand-text-size-100);
    line-height: var(--brand-text-lineHeight-100);
  }

  .SubdomainNavBar-menu-button-label {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
  }

  .SubdomainNavBar-menu-button-icon {
    flex: none;
    align-items: stretch;
    justify-content: center;
    width: var(--base-size-16);
    height: var(--base-size-16);
    overflow: visible;
  }

  .SubdomainNavBar-menu-button-icon .SubdomainNavBar-menu-button-bar {
    width: var(--base-size-16);
    height: var(--base-size-2);
    margin: 0;
    transform-origin: center;
  }

  .SubdomainNavBar-menu-button--close .SubdomainNavBar-menu-button-bar:nth-of-type(1) {
    transform: translateY(calc(var(--base-size-6) - var(--brand-borderWidth-thin))) rotate(45deg);
  }

  .SubdomainNavBar-menu-button--close .SubdomainNavBar-menu-button-bar:nth-of-type(3) {
    transform: translateY(calc(var(--brand-borderWidth-thin) - var(--base-size-6))) rotate(-45deg);
  }

  .SubdomainNavBar-menu-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow-y: auto;
    position: absolute;
    inset-inline: 0;
    top: var(--SubdomainNavBar-height);
    z-index: 2;
    box-sizing: border-box;
    width: 100%;
    max-height: calc(100vh - var(--SubdomainNavBar-height) - var(--SubdomainNavBar-menu-offset-block-start, 0px));
    background-color: var(--brand-color-canvas-default);
    border-inline: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
    border-block-end: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
    animation: fade-in-down var(--brand-animation-duration-default);
    animation-timing-function: var(--brand-animation-easing-default);
  }

  .SubdomainNavBar--variant-default .SubdomainNavBar-menu-wrapper {
    inset-inline-start: auto;
    inset-inline-end: 0;
    width: max(100%, calc(var(--base-size-128) * 3));
    min-width: 0;
  }

  .SubdomainNavBar-menu-wrapper--close {
    display: none;
  }

  .SubdomainNavBar-primary-nav-list--visible {
    display: grid;
    gap: var(--brand-borderWidth-thin);
    margin: 0;
    padding: var(--base-size-8) var(--base-size-16);
  }

  .SubdomainNavBar-menu-wrapper-footer {
    flex: none;
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .SubdomainNavBar .SubdomainNavBar-menu-wrapper .SubdomainNavBar-link {
    box-sizing: border-box;
    width: 100%;
    min-height: var(--base-size-40);
    padding: var(--base-size-8) var(--base-size-12);
    color: var(--brand-color-text-muted);
    background-color: var(--brand-color-canvas-default);
    border-radius: var(--brand-borderRadius-medium);
    font-size: var(--brand-text-size-100);
    line-height: var(--brand-text-lineHeight-200);
  }

  .SubdomainNavBar
    .SubdomainNavBar-menu-wrapper
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item) {
    width: 100%;
    height: auto;
  }

  .SubdomainNavBar
    .SubdomainNavBar-menu-wrapper
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link {
    padding: var(--base-size-8) var(--base-size-12);
  }

  .SubdomainNavBar
    .SubdomainNavBar-menu-wrapper
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link
    > .SubdomainNavBar-link-content {
    width: 100%;
    padding: 0;
    background-color: transparent;
    border-radius: 0;
  }

  .SubdomainNavBar
    .SubdomainNavBar-menu-wrapper
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link:hover
    > .SubdomainNavBar-link-content,
  .SubdomainNavBar
    .SubdomainNavBar-menu-wrapper
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link:focus
    > .SubdomainNavBar-link-content,
  .SubdomainNavBar
    .SubdomainNavBar-menu-wrapper
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link:focus-visible
    > .SubdomainNavBar-link-content,
  .SubdomainNavBar
    .SubdomainNavBar-menu-wrapper
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link:active
    > .SubdomainNavBar-link-content,
  .SubdomainNavBar
    .SubdomainNavBar-menu-wrapper
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link[aria-current]:not([aria-current='false'])
    > .SubdomainNavBar-link-content {
    background-color: transparent;
    outline: none;
  }

  .SubdomainNavBar
    .SubdomainNavBar-menu-wrapper
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link:hover,
  .SubdomainNavBar
    .SubdomainNavBar-menu-wrapper
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link:focus,
  .SubdomainNavBar
    .SubdomainNavBar-menu-wrapper
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link:focus-visible,
  .SubdomainNavBar
    .SubdomainNavBar-menu-wrapper
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link:active,
  .SubdomainNavBar
    .SubdomainNavBar-menu-wrapper
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link[aria-current]:not([aria-current='false']) {
    color: var(--brand-color-text-default);
    background-color: var(--brand-color-canvas-subtle);
  }

  .SubdomainNavBar
    .SubdomainNavBar-menu-wrapper
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link:focus,
  .SubdomainNavBar
    .SubdomainNavBar-menu-wrapper
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link:focus-visible {
    box-shadow: inset 0 0 0 var(--brand-borderWidth-thick) var(--brand-color-focus);
    outline: none;
  }

  .SubdomainNavBar .SubdomainNavBar-menu-wrapper .SubdomainNavBar-link--title {
    font-size: var(--brand-text-size-100);
    line-height: var(--brand-text-lineHeight-200);
  }

  .SubdomainNavBar-menu-wrapper .SubdomainNavBar-leading-component,
  .SubdomainNavBar-menu-wrapper .SubdomainNavBar-trailing-component {
    box-sizing: border-box;
    flex: none;
    width: 100%;
    height: auto;
    margin-inline: 0;
    padding: var(--base-size-16);
  }

  .SubdomainNavBar .SubdomainNavBar-menu-wrapper .SubdomainNavBar-button-area {
    display: flex;
    width: 100%;
    height: auto;
    padding: 0;
  }

  .SubdomainNavBar-menu-wrapper .SubdomainNavBar-button-area-inner {
    box-sizing: border-box;
    flex-direction: column;
    width: 100%;
    margin-block: var(--base-size-24);
    padding-inline: var(--base-size-16);
  }
}

.SubdomainNavBar .SubdomainNavBar-menu-wrapper-footer--has-leading-item,
.SubdomainNavBar
  .SubdomainNavBar-menu-wrapper
  .SubdomainNavBar-leading-component
  + .SubdomainNavBar-primary-nav-list--visible,
.SubdomainNavBar
  .SubdomainNavBar-menu-wrapper-footer
  .SubdomainNavBar-trailing-component
  + .SubdomainNavBar-button-area {
  border-block-start: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
}

.SubdomainNavBar-button-area {
  display: inline-flex;
  gap: var(--base-size-16);
  align-items: center;
  min-width: 0;
}

.SubdomainNavBar-button-area-inner {
  display: flex;
  gap: var(--base-size-8);
  min-width: 0;
}

@media screen and (max-width: 48rem) {
  .SubdomainNavBar-button-area {
    display: none;
  }

  .SubdomainNavBar-button-area--visible {
    display: flex;
    left: 0;
    z-index: 99;
    width: 100%;
    height: auto;
    animation-name: fade-in-down-staggered;
    animation-duration: 500ms;
    animation-delay: calc(10 * 80ms);
    animation-fill-mode: both;
    animation-timing-function: var(--brand-animation-easing-default);
  }

  .SubdomainNavBar-inner-container--search-open .SubdomainNavBar-button-area--visible {
    display: none;
  }

  .SubdomainNavBar-button-area-inner {
    width: 100%;
    box-sizing: border-box;
    flex-direction: column;
    margin-block: var(--base-size-24);
    padding-inline: var(--base-size-24);
    min-width: 0;
  }
}

.SubdomainNavBar-cta-button {
  white-space: nowrap;
}

.SubdomainNavBar .SubdomainNavBar-menu-wrapper {
  top: var(--SubdomainNavBar-height);
  height: auto;
  max-height: calc(100vh - var(--SubdomainNavBar-height) - var(--SubdomainNavBar-menu-offset-block-start, 0px));
}

.SubdomainNavBar-menu-backdrop {
  position: fixed;
  inset-block-start: calc(var(--SubdomainNavBar-height) + var(--SubdomainNavBar-menu-offset-block-start, 0px));
  inset-block-end: 0;
  inset-inline: 0;
  z-index: 1;
  background-color: var(--brand-SubdomainNavBar-searchDialog-backdropColor);
  animation: fade-in var(--brand-animation-duration-fast) var(--brand-animation-easing-glide);
}

.SubdomainNavBar .SubdomainNavBar-inner-container--search-open {
  animation: none;
}

@media screen and (max-width: 47.99rem) {
  .SubdomainNavBar {
    --SubdomainNavBar-mobile-control-size: var(--base-size-48);
  }

  .SubdomainNavBar .SubdomainNavBar-inner-container {
    height: 100%;
    padding: 0;
  }

  .SubdomainNavBar .SubdomainNavBar-title-area {
    height: 100%;
    max-width: none;
    gap: 0;
    padding: 0;
  }

  .SubdomainNavBar .SubdomainNavBar-title-area > li {
    display: flex;
    align-items: center;
    height: 100%;
    padding-inline: 0 var(--base-size-16);
  }

  .SubdomainNavBar .SubdomainNavBar-title-area > li:first-child {
    justify-content: center;
    width: auto;
    padding-inline: var(--base-size-16) var(--base-size-8);
  }

  .SubdomainNavBar .SubdomainNavBar-logo-mark {
    justify-content: center;
  }

  .SubdomainNavBar .SubdomainNavBar-logo-mark svg {
    width: var(--base-size-24);
    height: var(--base-size-24);
  }

  .SubdomainNavBar .SubdomainNavBar-title {
    padding-inline-end: 0;
  }

  .SubdomainNavBar .SubdomainNavBar-title > * {
    font-size: var(--brand-text-size-100);
    line-height: var(--brand-text-lineHeight-100);
  }

  .SubdomainNavBar .SubdomainNavBar-secondary-nav {
    height: 100%;
  }

  .SubdomainNavBar .SubdomainNavBar-search-trigger {
    flex: 0 0 var(--SubdomainNavBar-mobile-control-size);
    justify-content: center;
    width: var(--SubdomainNavBar-mobile-control-size);
    height: 100%;
    padding-inline: 0;
    border-inline-start: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
  }

  .SubdomainNavBar .SubdomainNavBar-search-input-button,
  .SubdomainNavBar .SubdomainNavBar-mobile-menu-button {
    box-sizing: border-box;
    display: inline-flex;
    flex: 0 0 var(--SubdomainNavBar-mobile-control-size);
    width: var(--SubdomainNavBar-mobile-control-size);
    flex-direction: column;
    height: 100%;
    padding-inline: var(--base-size-16);
    border-radius: 0;
  }

  .SubdomainNavBar .SubdomainNavBar-mobile-menu-button {
    gap: calc(var(--base-size-4) - var(--brand-borderWidth-thin));
    border-inline-start: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
  }

  .SubdomainNavBar .SubdomainNavBar-menu-button-bar {
    width: var(--base-size-16);
    height: var(--base-size-2);
    margin: 0 auto;
    transform-origin: center;
  }

  .SubdomainNavBar .SubdomainNavBar-menu-button--close .SubdomainNavBar-menu-button-bar:nth-of-type(1) {
    transform: translateY(calc(var(--base-size-6) - var(--brand-borderWidth-thin))) rotate(45deg);
  }

  .SubdomainNavBar .SubdomainNavBar-menu-button--close .SubdomainNavBar-menu-button-bar:nth-of-type(3) {
    transform: translateY(calc(var(--brand-borderWidth-thin) - var(--base-size-6))) rotate(-45deg);
  }

  .SubdomainNavBar .SubdomainNavBar-menu-wrapper .SubdomainNavBar-link {
    padding: var(--base-size-16);
    font-size: var(--brand-text-size-100);
    line-height: var(--brand-text-lineHeight-100);
  }

  .SubdomainNavBar .SubdomainNavBar-menu-wrapper .SubdomainNavBar-link--title {
    font-size: var(--brand-text-size-100);
    line-height: var(--brand-text-lineHeight-100);
  }

  .SubdomainNavBar .SubdomainNavBar-menu-wrapper .SubdomainNavBar-button-area-inner {
    padding-inline: var(--base-size-16);
  }
}

@media screen and (min-width: 48rem) {
  .SubdomainNavBar .SubdomainNavBar-inner-container {
    height: 100%;
    max-width: none;
    padding: 0;
  }

  .SubdomainNavBar .SubdomainNavBar-title-area {
    height: 100%;
    max-width: none;
    gap: 0;
    padding: 0;
  }

  .SubdomainNavBar .SubdomainNavBar-title-area > li {
    display: flex;
    align-items: center;
    height: 100%;
    padding-inline: 0 var(--base-size-16);
    border-inline-end: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
  }

  .SubdomainNavBar .SubdomainNavBar-title-area > li:first-child {
    justify-content: center;
    width: auto;
    padding-inline: var(--base-size-16) var(--base-size-8);
    border-inline-end: 0;
  }

  .SubdomainNavBar--variant-default.SubdomainNavBar--has-title .SubdomainNavBar-title-area > li:first-child {
    padding-inline-end: 0;
  }

  .SubdomainNavBar--variant-default.SubdomainNavBar--has-title .SubdomainNavBar-title-area > li:first-child::after {
    margin-inline-end: 0;
  }

  .SubdomainNavBar--variant-default.SubdomainNavBar--has-title .SubdomainNavBar-title-item {
    padding-inline: var(--base-size-24);
  }

  .SubdomainNavBar .SubdomainNavBar-logo-mark {
    justify-content: center;
  }

  .SubdomainNavBar .SubdomainNavBar-logo-mark svg {
    width: var(--base-size-24);
    height: var(--base-size-24);
  }

  .SubdomainNavBar .SubdomainNavBar-title {
    padding-inline-end: 0;
  }

  .SubdomainNavBar .SubdomainNavBar-title > * {
    font-size: var(--base-size-16);
    line-height: var(--base-size-16);
  }

  .SubdomainNavBar .SubdomainNavBar-primary-nav,
  .SubdomainNavBar .SubdomainNavBar-primary-nav-overflow,
  .SubdomainNavBar .SubdomainNavBar-primary-nav-list,
  .SubdomainNavBar .SubdomainNavBar-secondary-nav,
  .SubdomainNavBar .SubdomainNavBar-button-area,
  .SubdomainNavBar .SubdomainNavBar-leading-component,
  .SubdomainNavBar .SubdomainNavBar-trailing-component {
    height: 100%;
  }

  .SubdomainNavBar .SubdomainNavBar-leading-component,
  .SubdomainNavBar .SubdomainNavBar-trailing-component {
    padding-inline: var(--base-size-16);
    margin-inline: 0;
  }

  .SubdomainNavBar .SubdomainNavBar-leading-component {
    border-inline: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
  }

  .SubdomainNavBar .SubdomainNavBar-primary-nav-list {
    align-items: stretch;
    margin: 0;
    block-size: 100%;
    max-block-size: 100%;
    padding-inline-start: var(--base-size-16);
  }

  .SubdomainNavBar .SubdomainNavBar-primary-nav-overflow {
    margin: 0;
  }

  .SubdomainNavBar .SubdomainNavBar-primary-nav-list-item {
    display: flex;
    align-items: center;
    height: 100%;
  }

  .SubdomainNavBar .SubdomainNavBar-link {
    align-items: center;
    height: 100%;
    padding: 0 var(--base-size-16);
    color: var(--brand-color-text-muted);
    font-size: var(--brand-text-size-100);
    line-height: var(--brand-text-lineHeight-200);
  }

  .SubdomainNavBar
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link,
  .SubdomainNavBar .SubdomainNavBar-more-link {
    padding-inline: var(--base-size-4);
  }

  .SubdomainNavBar
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link
    > .SubdomainNavBar-link-content,
  .SubdomainNavBar .SubdomainNavBar-more-link > .SubdomainNavBar-link-content {
    gap: var(--base-size-4);
    padding: var(--base-size-4) var(--base-size-12);
    background-color: transparent;
    border-radius: var(--brand-borderRadius-full);
  }

  .SubdomainNavBar
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link:hover
    > .SubdomainNavBar-link-content,
  .SubdomainNavBar
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link:active
    > .SubdomainNavBar-link-content,
  .SubdomainNavBar
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link[aria-current]:not([aria-current='false'])
    > .SubdomainNavBar-link-content,
  .SubdomainNavBar .SubdomainNavBar-more-link:hover > .SubdomainNavBar-link-content,
  .SubdomainNavBar .SubdomainNavBar-more-link:active > .SubdomainNavBar-link-content,
  .SubdomainNavBar .SubdomainNavBar-more-link[aria-expanded='true'] > .SubdomainNavBar-link-content {
    background-color: var(--brand-SubdomainNavBar-color-link-bgColor);
  }

  .SubdomainNavBar
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link:focus-visible,
  .SubdomainNavBar .SubdomainNavBar-more-link:focus-visible {
    outline: none;
  }

  .SubdomainNavBar
    .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
    > .SubdomainNavBar-link:focus-visible
    > .SubdomainNavBar-link-content,
  .SubdomainNavBar .SubdomainNavBar-more-link:focus-visible > .SubdomainNavBar-link-content {
    outline: var(--brand-borderWidth-thick) solid var(--brand-color-focus);
    outline-offset: var(--base-size-2);
  }

  @media (prefers-reduced-motion: no-preference) {
    .SubdomainNavBar
      .SubdomainNavBar-primary-nav-list-item:not(.SubdomainNavBar-overflow-menu-item)
      > .SubdomainNavBar-link
      > .SubdomainNavBar-link-content,
    .SubdomainNavBar .SubdomainNavBar-more-link > .SubdomainNavBar-link-content {
      transition: background-color var(--brand-animation-duration-fast) var(--brand-animation-easing-default);
    }
  }

  .SubdomainNavBar .SubdomainNavBar-search-trigger {
    height: 100%;
    padding-inline: var(--base-size-16);
  }

  .SubdomainNavBar .SubdomainNavBar-search-trigger--has-trailing-item {
    border-inline-end: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
  }

  .SubdomainNavBar .SubdomainNavBar-button-area {
    align-items: center;
    padding-inline: var(--base-size-16);
  }

  .SubdomainNavBar .SubdomainNavBar-button-area--has-leading-item {
    border-inline-start: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
  }
}

@media screen and (min-width: 63.25rem) {
  .SubdomainNavBar .SubdomainNavBar-search-trigger {
    --SubdomainNavBar-search-input-width: clamp(
      calc(var(--base-size-128) + var(--base-size-32)),
      28.5vw,
      calc(var(--base-size-128) * 3 + var(--base-size-36))
    );

    align-self: stretch;
  }

  .SubdomainNavBar--variant-default.SubdomainNavBar--has-primary-nav {
    border-block-end: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
    height: calc(var(--SubdomainNavBar-height) + var(--base-size-48));
  }

  .SubdomainNavBar--variant-default::after {
    inset-block-start: var(--SubdomainNavBar-height);
  }

  .SubdomainNavBar--variant-default .SubdomainNavBar-inner-container {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-rows: var(--SubdomainNavBar-height);
  }

  .SubdomainNavBar--variant-default.SubdomainNavBar--has-primary-nav .SubdomainNavBar-inner-container {
    grid-template-rows: var(--SubdomainNavBar-height) var(--base-size-48);
  }

  .SubdomainNavBar--variant-default .SubdomainNavBar-title-navigation {
    grid-column: 1;
    grid-row: 1;
    min-width: 0;
  }

  .SubdomainNavBar--variant-default .SubdomainNavBar-primary-nav {
    grid-column: 1 / -1;
    grid-row: 2;
    min-width: 0;
    border-block-start: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
  }

  .SubdomainNavBar--variant-default .SubdomainNavBar-primary-nav-list {
    padding-inline-start: calc(var(--base-size-64) + var(--brand-borderWidth-thin));
  }

  .SubdomainNavBar--variant-default .SubdomainNavBar-inner-container > .SubdomainNavBar-leading-component {
    grid-column: 2;
    grid-row: 1;
    justify-self: start;
    border-inline-start: 0;
  }

  .SubdomainNavBar--variant-default .SubdomainNavBar-inner-container > .SubdomainNavBar-secondary-nav {
    grid-column: 3;
    grid-row: 1;
  }

  .SubdomainNavBar--variant-default .SubdomainNavBar-search-trigger {
    border-inline-start: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
  }
}

.SubdomainNavBar--variant-default .SubdomainNavBar-search-input-button {
  justify-content: center;
  gap: 0;
  width: var(--base-size-32);
  padding: var(--base-size-8);
  background-color: transparent;
}

.SubdomainNavBar--variant-default .SubdomainNavBar-search-input-button-placeholder {
  gap: 0;
}

.SubdomainNavBar--variant-default .SubdomainNavBar-search-input-button-placeholder-text,
.SubdomainNavBar--variant-default .SubdomainNavBar-search-input-button-shortcut {
  display: none;
}

@media screen and (min-width: 48rem) {
  .SubdomainNavBar--variant-default .SubdomainNavBar-search-trigger {
    flex: 0 0 var(--base-size-48);
    width: var(--base-size-48);
    min-width: var(--base-size-48);
  }

  .SubdomainNavBar--variant-default .SubdomainNavBar-search-trigger {
    padding-inline: 0;
  }

  .SubdomainNavBar--variant-default .SubdomainNavBar-search-input-button {
    width: 100%;
    block-size: 100%;
    padding-inline: var(--base-size-16);
    border-radius: 0;
  }
}

@media screen and (min-width: 1024px) {
  .SubdomainNavBar-cta-button--secondary {
    display: inline-flex;
  }
}

/*
 * Search dialog control area
 */
.SubdomainNavBar-search-form {
  display: flex;
  width: 100%;
}

.SubdomainNavBar-search-input-area {
  display: flex;
  align-items: center;
  gap: var(--base-size-8);
  width: 100%;
}

.SubdomainNavBar-search-close-button {
  cursor: pointer;
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  min-block-size: var(--base-size-32);
  padding: var(--base-size-8) var(--base-size-12);
  color: var(--brand-color-text-default);
  background-color: var(--brand-color-canvas-subtle);
  border: 0;
  border-radius: var(--brand-borderRadius-medium);
  font-family: var(--brand-body-fontFamily);
  font-size: var(--brand-text-size-100);
  font-weight: var(--base-text-weight-medium);
  line-height: var(--base-size-14);
  letter-spacing: var(--brand-text-letterSpacing-100);
}

.SubdomainNavBar-search-close-button:hover {
  background-color: var(--brand-color-canvas-inset);
}

.SubdomainNavBar-search-close-button:focus-visible {
  outline: var(--base-size-4) solid var(--brand-color-focus);
  outline-offset: var(--base-size-2);
}

.SubdomainNavBar-search-dialog-control-area {
  height: var(--base-size-40);
  max-width: none;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  background: transparent;
}

.SubdomainNavBar-search-text-input {
  border-color: transparent;
}

.SubdomainNavBar-search-results-area {
  width: 100%;
}

.SubdomainNavBar-search-results-container {
  animation: fade-in-down 500ms;
  padding: var(--base-size-24);
  background-color: var(--base-color-scale-white-0);
  border-radius: var(--brand-borderRadius-large);
  box-shadow: 0px 8px 24px rgba(1, 4, 9, 0.15);
  max-height: 514px;
  height: 70vh;
  max-width: 570px;
  margin: 0 auto;
  width: 80%;
}

.SubdomainNavBar-search-results {
  overflow-y: scroll;
  list-style: none;
  padding: 0 var(--base-size-24) 0 0;
  margin: 0;
  height: 90%;
}

.SubdomainNavBar-search-results-heading {
  display: block;
  font-weight: var(--brand-heading-weight-600);
  font-size: var(--base-size-20);
  line-height: var(--base-size-24);
  margin-bottom: var(--base-size-20);
  padding: 0 var(--base-size-8);
  color: var(--base-color-scale-black-0);
}

.SubdomainNavBar-search-result-group,
.SubdomainNavBar-search-result-group-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.SubdomainNavBar-search-result-group + .SubdomainNavBar-search-result-group {
  margin-block-start: var(--base-size-24);
}

.SubdomainNavBar-search-result-group-heading {
  display: block;
  margin: 0 0 var(--base-size-20);
  padding: 0 var(--base-size-8);
  color: var(--brand-color-text-muted);
  font-weight: var(--brand-heading-weight-600);
  font-size: var(--brand-text-size-100);
  line-height: var(--brand-text-lineHeight-100);
}

.SubdomainNavBar-search-result-item {
  padding: var(--base-size-16) var(--base-size-8);
  border-bottom: solid var(--brand-borderWidth-thin, 1px)
    var(--brand-SubdomainNavBar-search-results-border-default, #b7bfc7);
}

.SubdomainNavBar-search-result-item-container a[aria-selected='true'] {
  outline: var(--brand-color-focus) auto var(--brand-borderWidth-thick);
}

.SubdomainNavBar-search-result-item-container {
  letter-spacing: var(--brand-heading-letterSpacing-400);
  font-size: var(--brand-text-size-400);
}

.SubdomainNavBar-search-result-item-container a {
  display: block;
  color: var(--base-color-scale-black-0);
  text-decoration: none;
  margin: 0 0 var(--base-size-12);
  font-size: var(--base-size-16);
  font-weight: var(--brand-heading-weight-400);
  font-family: var(--brand-heading-fontFamily);
  line-height: var(--brand-text-lineHeight-400);
}

.SubdomainNavBar-search-result-item-container a svg {
  flex: none;
}

.SubdomainNavBar-search-result-item-container a:hover {
  text-decoration: none;
}

.SubdomainNavBar-search-result-item-desc {
  color: var(--base-color-scale-black-0);
}

.SubdomainNavBar-search-dialog {
  position: fixed;
  inset-block-start: calc(var(--base-size-64) + var(--base-size-8));
  inset-block-end: auto;
  inset-inline: calc(var(--base-size-64) + var(--base-size-8));
  z-index: 99;
  width: min(
    calc(100vw - ((var(--base-size-64) + var(--base-size-8)) * 2)),
    calc((var(--base-size-128) * 6) + var(--base-size-36))
  );
  max-height: calc(100vh - ((var(--base-size-64) + var(--base-size-8)) * 2));
  height: auto;
  margin-inline: auto;
  padding: var(--base-size-16);
  overflow: hidden;
  color: var(--brand-color-text-default);
  background-color: var(--brand-color-canvas-default);
  border: var(--brand-borderWidth-thin) solid var(--brand-color-border-default);
  border-radius: var(--brand-borderRadius-large);
  box-shadow: 0 0 calc(var(--base-size-32) + var(--base-size-2)) var(--brand-SubdomainNavBar-searchDialog-shadowColor);
}

.SubdomainNavBar-search-dialog--has-results {
  padding-block-end: var(--base-size-32);
}

.SubdomainNavBar-search-dialog::backdrop {
  background-color: var(--brand-SubdomainNavBar-searchDialog-backdropColor);
  -webkit-backdrop-filter: blur(var(--base-size-24));
  backdrop-filter: blur(var(--base-size-24));
}

.SubdomainNavBar-search-dialog .SubdomainNavBar-search-input-area {
  gap: var(--base-size-16);
}

.SubdomainNavBar-search-dialog .SubdomainNavBar-search-form {
  min-width: 0;
}

.SubdomainNavBar-search-dialog .SubdomainNavBar-search-form section > span:focus-within {
  border-color: transparent;
  box-shadow: none;
}

.SubdomainNavBar-search-dialog .SubdomainNavBar-search-text-input {
  color: var(--brand-color-text-default);
  font-size: calc(var(--base-size-16) + var(--base-size-2));
  font-weight: var(--base-text-weight-medium);
  line-height: var(--brand-text-lineHeight-300);
  letter-spacing: var(--brand-text-letterSpacing-100);
}

.SubdomainNavBar-search-dialog .SubdomainNavBar-search-close-button {
  background-color: var(--brand-color-border-muted);
}

.SubdomainNavBar-search-dialog .SubdomainNavBar-search-results-area--visible {
  margin-block-start: var(--base-size-24);
  padding-block-start: var(--base-size-24);
  border-block-start: var(--brand-borderWidth-thin) dashed var(--brand-color-border-muted);
}

.SubdomainNavBar-search-dialog .SubdomainNavBar-search-results-container {
  width: 100%;
  height: auto;
  max-width: none;
  max-height: calc(100vh - (((var(--base-size-64) + var(--base-size-8)) * 2) + var(--base-size-128)));
  padding: 0;
  margin: 0;
  overflow-y: auto;
  background-color: transparent;
  border-radius: 0;
  box-shadow: none;
  animation: none;
}

.SubdomainNavBar-search-dialog .SubdomainNavBar-search-results {
  height: auto;
  padding: 0;
  margin: 0;
  overflow: visible;
}

.SubdomainNavBar-search-dialog .SubdomainNavBar-search-results-heading,
.SubdomainNavBar-search-dialog .SubdomainNavBar-search-result-group-heading {
  margin-block-end: var(--base-size-20);
  padding: 0;
  color: var(--brand-color-text-muted);
  font-size: var(--brand-text-size-100);
  font-weight: var(--brand-heading-weight-600);
  line-height: var(--brand-text-lineHeight-100);
}

.SubdomainNavBar-search-dialog .SubdomainNavBar-search-result-group + .SubdomainNavBar-search-result-group {
  margin-block-start: var(--base-size-24);
}

.SubdomainNavBar-search-dialog .SubdomainNavBar-search-result-item {
  padding: var(--base-size-16) var(--base-size-4);
  border-block-end: 0;
  border-block-start: var(--brand-borderWidth-thin) dashed var(--brand-color-border-muted);
}

.SubdomainNavBar-search-dialog
  .SubdomainNavBar-search-result-group-list
  > .SubdomainNavBar-search-result-item:first-child,
.SubdomainNavBar-search-dialog .SubdomainNavBar-search-results > .SubdomainNavBar-search-result-item:first-child {
  padding-block-start: var(--base-size-12);
  border-block-start: 0;
}

.SubdomainNavBar-search-dialog .SubdomainNavBar-search-result-item-container {
  font-size: var(--brand-text-size-300);
  letter-spacing: var(--brand-text-letterSpacing-100);
}

.SubdomainNavBar-search-dialog .SubdomainNavBar-search-result-item-container a {
  display: flex;
  align-items: center;
  gap: calc(var(--base-size-8) + var(--base-size-2));
  width: 100%;
  margin: 0;
  color: var(--brand-color-text-default);
  font-size: var(--brand-text-size-300);
  font-weight: var(--brand-heading-weight-600);
  line-height: var(--brand-text-lineHeight-300);
  letter-spacing: var(--brand-text-letterSpacing-100);
}

.SubdomainNavBar-search-dialog .SubdomainNavBar-search-result-item-container a span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.SubdomainNavBar-search-dialog .SubdomainNavBar-search-result-item-desc,
.SubdomainNavBar-search-dialog .SubdomainNavBar-search-result-item-meta {
  display: none;
}

@media screen and (max-width: 47.99rem) {
  .SubdomainNavBar-search-dialog {
    inset-block-start: var(--base-size-16);
    inset-block-end: auto;
    inset-inline: var(--base-size-16);
    width: calc(100vw - (var(--base-size-16) * 2));
    max-height: calc(100vh - (var(--base-size-16) * 2));
    height: auto;
  }
}

.SubdomainNavBar-skip-to-content {
  position: absolute;
  inset-block-start: calc(var(--SubdomainNavBar-height) / 2);
  inset-inline-start: var(--base-size-16);
  z-index: 100;
  transform: translateY(-50%);
}

/*
 * Reusable animations
 */
@keyframes fade-in-down {
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes fade-in-down-overflow {
  0% {
    opacity: 0;
    transform: translateY(calc(0px - var(--base-size-24)));
  }
  100% {
    opacity: 1;
    transform: translateY(calc(0px - var(--base-size-4)));
  }
}

@keyframes fade-out {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

@keyframes fade-in-down-staggered {
  0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }

  100% {
    opacity: 1;
  }
}

/* Remove all animations, transitions and smooth scroll for people that prefer not to see them */
@media (prefers-reduced-motion: reduce) {
  .SubdomainNavBar,
  .SubdomainNavBar::before,
  .SubdomainNavBar::after,
  .SubdomainNavBar * {
    animation: none !important;
    transition: none !important;
    scroll-behavior: auto !important;
  }
}
